import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import api from '@/shared/api';
import config from '@/config';
import AuthService, { privileges, functionalities } from '@/shared/services/AuthService';
import SignalRService from '@/shared/services/SignalRService';
import DocumentService from '@/shared/services/DocumentService';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import FileUpload from '@/shared/components/input/FileUpload';
import FileIcon from '@/shared/components/FileIcon';
import DocumentDetailsModal from '@/modules/documents/details/DocumentDetailsModal';
import DocumentSelectorModal from '@/shared/components/DocumentSelectorModal';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import FormattedListValue from '@/shared/components/FormattedListValue';
import Button from '@/shared/components/Button';
import YesNoCancelModal from '@/shared/components/YesNoCancelModal';
import { FaRegFolderOpen } from 'react-icons/fa';
import { CanCreate, CanDelete, CanUpdate } from '@/shared/components/security';
import EditableDocumentTypeSelect from '@/modules/documents/EditableDocumentTypeSelect';
const messages = defineMessages({
    Documents: { id: 'Documents', defaultMessage: 'Documents' },
    AddDocument: { id: 'AddDocument', defaultMessage: 'Add document' },
    DragDropFiles: { id: 'DragDropFiles', defaultMessage: 'Drag & drop files here or' },
    Delete: { id: 'Delete', defaultMessage: 'Delete' },
    DocumentDate: { id: 'DocumentDate', defaultMessage: 'Document date' },
    DocumentType: { id: 'DocumentType', defaultMessage: 'Document type' },
    Name: { id: 'Name', defaultMessage: 'Name' },
    OpenDocument: { id: 'OpenDocument', defaultMessage: 'Open document' },
    ChooseDocumentInAdminPulse: {
        id: 'ChooseDocumentInAdminPulse',
        defaultMessage: 'Choose document in AdminPulse',
    },
    ConfirmDeleteTitle: { id: 'comfirmDeleteTitle', defaultMessage: 'Delete?' },
    ConfirmDeleteDocumentOrLink: {
        id: 'msgConfirmDeleteDocumentOrLink',
        defaultMessage: 'Do you want to delete the document (choose yes) or keep the document in DMS but remove the link (choose no)',
    },
    DeleteDocument: { id: 'DeleteDocument', defaultMessage: 'Delete document' },
    RemoveLink: { id: 'RemoveLink', defaultMessage: 'Remove link' },
    UploadFailed: { id: 'MsgUploadAttachmentFailed', defaultMessage: 'Upload failed' },
    ExtractDocumentData: { id: 'ExtractDocumentData', defaultMessage: 'Extract data (AI)' },
});
const FiscalDocumentList = ({ relationId, linkType, linkId, documents, onDocumentsChanged, readOnly = false, uploadNewDocumentsRef, onExtractDocument, extractingDocumentId, }) => {
    const intl = useIntl();
    const [newDocuments, setNewDocuments] = useState([]);
    const [previewDocumentId, setPreviewDocumentId] = useState(null);
    const [showDocumentSelector, setShowDocumentSelector] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const newDocumentsRef = useRef(newDocuments);
    newDocumentsRef.current = newDocuments;
    const canReadDocs = AuthService.hasPermission({
        functionality: 'Documents',
        privilege: privileges.Read,
    });
    const canExtractAI = !!onExtractDocument &&
        AuthService.hasFeature('AI') &&
        AuthService.hasPermission({ functionality: functionalities.AI, privilege: privileges.Update });
    useEffect(() => {
        const handler = (updatedDoc) => {
            if (!updatedDoc)
                return;
            const idx = documents.findIndex(x => x.id === updatedDoc.id);
            if (idx >= 0) {
                const updated = documents.map(x => (x.id === updatedDoc.id ? Object.assign(Object.assign({}, x), updatedDoc) : x));
                onDocumentsChanged(updated);
                return;
            }
            const newIdx = newDocumentsRef.current.findIndex((x) => x.id === updatedDoc.id);
            if (newIdx >= 0) {
                setNewDocuments(prev => prev.map(x => (x.id === updatedDoc.id ? Object.assign(Object.assign({}, x), updatedDoc) : x)));
            }
        };
        SignalRService.subscribe('documentUpdated', handler);
        return () => SignalRService.unsubscribe('documentUpdated', handler);
    }, [documents, onDocumentsChanged]);
    const linkStagedDocuments = useCallback(async (targetLinkId) => {
        const pending = newDocumentsRef.current;
        if (!pending || pending.length === 0)
            return [];
        const documentLinks = pending.map(doc => ({
            documentId: doc.id,
            linkId: targetLinkId,
            linkType,
        }));
        try {
            await api.base.post(`${config.appUrls.documents}/api/documents/linksmultiple`, documentLinks);
            const linkedDocs = [...pending];
            setNewDocuments([]);
            return linkedDocs;
        }
        catch (error) {
            handleHttpError(intl)(error);
            return [];
        }
    }, [intl, linkType]);
    useEffect(() => {
        if (uploadNewDocumentsRef) {
            uploadNewDocumentsRef.current = linkStagedDocuments;
        }
    }, [uploadNewDocumentsRef, linkStagedDocuments]);
    const handleFileUploaded = (error, file) => {
        if (error) {
            ToastrService.error(intl.formatMessage(messages.UploadFailed));
            return;
        }
        if (linkId) {
            onDocumentsChanged([...documents, file]);
        }
        else {
            setNewDocuments(prev => [...prev, file]);
        }
    };
    const handleDeleteExistingDocument = async (doc, deleteDocument) => {
        try {
            if (deleteDocument) {
                await api.base.delete(`${config.appUrls.documents}/api/documents/${doc.id}`);
            }
            else if (linkId) {
                await api.base.delete(`${config.appUrls.documents}/api/documents/Links/${doc.id}/${linkId}/${linkType}`);
            }
            onDocumentsChanged(documents.filter(x => x.id !== doc.id));
            ToastrService.success();
        }
        catch (error) {
            handleHttpError(intl)(error);
        }
        setDeleteTarget(null);
    };
    const handleDeleteNewDocument = (doc) => {
        setNewDocuments(prev => prev.filter(x => x !== doc));
    };
    const handleDocumentsSelected = (selectedDocs) => {
        setShowDocumentSelector(false);
        if (!selectedDocs || selectedDocs.length === 0)
            return;
        const existingIds = documents.map(x => x.id);
        const newDocIds = newDocuments.map(x => x.id);
        const genuinelyNew = selectedDocs.filter(x => !existingIds.includes(x.id) && !newDocIds.includes(x.id));
        if (genuinelyNew.length === 0)
            return;
        if (linkId) {
            const documentLinks = genuinelyNew.map(doc => ({
                documentId: doc.id,
                linkId,
                linkType,
            }));
            api.base
                .post(`${config.appUrls.documents}/api/documents/linksmultiple`, documentLinks)
                .then(() => {
                onDocumentsChanged([...documents, ...genuinelyNew]);
            })
                .catch(handleHttpError(intl));
        }
        else {
            setNewDocuments(prev => [...prev, ...genuinelyNew]);
        }
    };
    const handleDocumentPreviewSaved = (doc) => {
        setPreviewDocumentId(null);
        onDocumentsChanged(documents.map(x => (x.id === doc.id ? Object.assign(Object.assign({}, x), doc) : x)));
    };
    const handleUpdateDocumentType = (doc, documentType) => {
        return api.base
            .put(`${config.appUrls.documents}/api/documents/type`, { id: doc.id, documentType })
            .then(() => {
            if (documents.some(x => x.id === doc.id)) {
                onDocumentsChanged(documents.map(x => (x.id !== doc.id ? x : Object.assign(Object.assign({}, x), { documentType }))));
            }
            else {
                setNewDocuments(prev => prev.map(x => (x.id !== doc.id ? x : Object.assign(Object.assign({}, x), { documentType }))));
            }
            ToastrService.success();
        })
            .catch(handleHttpError(intl));
    };
    const uploadEndpoint = linkId
        ? `${config.appUrls.documents}/api/documents?documentType=-1&documentLinkType=${linkType}&linkId=${linkId}&relationId=${relationId}`
        : `${config.appUrls.documents}/api/documents?documentType=-1&relationId=${relationId}`;
    return (_jsxs("div", Object.assign({ className: "form-horizontal" }, { children: [previewDocumentId && (_jsx(DocumentDetailsModal, { documentId: previewDocumentId, show: true, onClose: () => setPreviewDocumentId(null), onSave: handleDocumentPreviewSaved })), _jsxs("div", Object.assign({ className: "form-group" }, { children: [_jsx("div", Object.assign({ className: "col-md-4" }, { children: _jsx("label", Object.assign({ className: "control-label" }, { children: intl.formatMessage(messages.Documents) })) })), _jsx("div", Object.assign({ className: "col-md-8" }, { children: !readOnly && (_jsx("div", Object.assign({ className: "margin10B" }, { children: _jsxs(CanCreate, Object.assign({ on: "Documents" }, { children: [_jsx("div", Object.assign({ className: "file-upload-wrapper margin5B" }, { children: _jsx(FileUpload, { endpoint: uploadEndpoint, relationId: relationId, onFileUploaded: handleFileUploaded, removeSuccessfullAfter: 500 }) })), _jsxs(Button, Object.assign({ btnClassName: "btn btn-default btn-grey btn-sm btn-block", onClick: () => setShowDocumentSelector(true) }, { children: [_jsx(FaRegFolderOpen, { className: "react-icon margin5R" }), intl.formatMessage(messages.ChooseDocumentInAdminPulse)] }))] })) }))) }))] })), (documents.length > 0 || newDocuments.length > 0) && (_jsxs("table", Object.assign({ className: "table margin10B" }, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", {}), linkId && _jsx("th", { children: intl.formatMessage(messages.DocumentDate) }), _jsx("th", { children: intl.formatMessage(messages.DocumentType) }), _jsx("th", { children: intl.formatMessage(messages.Name) }), _jsx("th", {})] }) }), _jsxs("tbody", { children: [documents.map(doc => (_jsxs("tr", { children: [_jsx("td", { children: doc.showSpinner && _jsx("i", { className: "fa fa-spinner fa-spin" }) }), linkId && (_jsx("td", Object.assign({ style: { maxWidth: 100 } }, { children: _jsx(FormattedDateShort, { value: doc.documentDate }) }))), _jsx("td", Object.assign({ style: { maxWidth: 100 } }, { children: doc.recognitionStatus === 1 || doc.recognitionStatus === 3 ? (_jsx("span", Object.assign({ className: "label label-info label-striped active" }, { children: _jsx(FormattedListValue, { list: "recognitionStatus", id: doc.recognitionStatus }) }))) : (_jsx(CanUpdate, Object.assign({ on: "Documents" }, { children: _jsx(EditableDocumentTypeSelect, { value: doc.documentType, onChange: (type) => handleUpdateDocumentType(doc, type), required: true, disabled: readOnly }) }))) })), _jsx("td", { children: canReadDocs && doc.scanStatus === 2 ? (_jsxs("a", Object.assign({ onClick: () => setPreviewDocumentId(doc.id), title: intl.formatMessage(messages.OpenDocument) }, { children: [_jsx(FileIcon, { document: doc }), doc.name] }))) : (_jsxs("span", { children: [_jsx(FileIcon, { document: doc }), doc.name] })) }), _jsxs("td", Object.assign({ className: "text-nowrap" }, { children: [canExtractAI &&
                                                doc.recognitionStatus !== 1 &&
                                                doc.recognitionStatus !== 3 &&
                                                DocumentService.supportsDataExtraction(doc.documentType) && (_jsx("span", { children: extractingDocumentId === doc.id ? (_jsx("i", { className: "fa fa-spinner fa-spin margin5R" })) : (_jsx("i", { onClick: () => onExtractDocument(doc.id), title: intl.formatMessage(messages.ExtractDocumentData), className: "fa fa-magic clickable margin5R" })) })), !readOnly && (_jsx(CanDelete, Object.assign({ on: "Documents" }, { children: _jsx("i", { onClick: () => setDeleteTarget({ document: doc, isNew: false }), title: intl.formatMessage(messages.Delete), className: "fa fa-trash-o clickable" }) })))] }))] }, doc.id))), newDocuments.map((doc, idx) => (_jsxs("tr", { children: [_jsx("td", { children: doc.showSpinner && _jsx("i", { className: "fa fa-spinner fa-spin" }) }), linkId && _jsx("td", {}), _jsx("td", Object.assign({ style: { maxWidth: 100 } }, { children: doc.recognitionStatus === 1 || doc.recognitionStatus === 3 ? (_jsx("span", Object.assign({ className: "label label-info label-striped active" }, { children: _jsx(FormattedListValue, { list: "recognitionStatus", id: doc.recognitionStatus }) }))) : (_jsx(CanUpdate, Object.assign({ on: "Documents" }, { children: _jsx(EditableDocumentTypeSelect, { value: doc.documentType, onChange: (type) => handleUpdateDocumentType(doc, type), required: true, disabled: readOnly }) }))) })), _jsx("td", { children: canReadDocs && doc.scanStatus === 2 ? (_jsxs("a", Object.assign({ onClick: () => setPreviewDocumentId(doc.id), title: intl.formatMessage(messages.OpenDocument) }, { children: [_jsx(FileIcon, { document: doc }), doc.name] }))) : (_jsxs("span", { children: [_jsx(FileIcon, { document: doc }), doc.name] })) }), _jsxs("td", Object.assign({ className: "text-nowrap" }, { children: [canExtractAI &&
                                                doc.recognitionStatus !== 1 &&
                                                doc.recognitionStatus !== 3 &&
                                                DocumentService.supportsDataExtraction(doc.documentType) && (_jsx("span", { children: extractingDocumentId === doc.id ? (_jsx("i", { className: "fa fa-spinner fa-spin margin5R" })) : (_jsx("i", { onClick: () => onExtractDocument(doc.id), title: intl.formatMessage(messages.ExtractDocumentData), className: "fa fa-magic clickable margin5R" })) })), !readOnly && (_jsx("i", { onClick: () => handleDeleteNewDocument(doc), title: intl.formatMessage(messages.Delete), className: "fa fa-trash-o clickable" }))] }))] }, `new-${idx}`)))] })] }))), showDocumentSelector && (_jsx(DocumentSelectorModal, { show: true, initialRelation: relationId, initialSelectedDocuments: documents, onDocumentsSelected: handleDocumentsSelected, onClose: () => setShowDocumentSelector(false) })), deleteTarget && !deleteTarget.isNew && (_jsx(YesNoCancelModal, { show: true, title: intl.formatMessage(messages.ConfirmDeleteTitle), message: intl.formatMessage(messages.ConfirmDeleteDocumentOrLink), onCancel: () => setDeleteTarget(null), onNo: () => handleDeleteExistingDocument(deleteTarget.document, false), onYes: () => handleDeleteExistingDocument(deleteTarget.document, true) }))] })));
};
export default FiscalDocumentList;
